import React, { useContext, useMemo } from "react";
import { Box, Stack, Typography, Alert, Button, Card, CardContent } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useEnglishText } from "../../../text/useEnglishText.js";

import TrialExpiredCard from "../../../components/app/licenseRequests/TrialExpiredCard";
import SubscriptionSummaryCard from "../../../components/app/licenseRequests/SubscriptionSummaryCard";
import SystemCredentialsCard from "../../../components/app/licenseRequests/SystemCredentialsCard";
import LicensePurchaseForm from "./LicensePurchaseForm";
import { AuthContext } from "../../../context/AuthContext";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import { ClipboardCheck } from "lucide-react";
import "../../../styles/LicenseRequests.css";

const LicenseRequestsPage = () => {
  const { user } = useContext(AuthContext);
  const { t } = useEnglishText();

  const isEmptyString = (val) => !val || String(val).trim() === "";

  // مشخصات پروفایل کاربر
  const fullName = user?.fullName || user?.FULL_NAME || "";
  const email = user?.email || user?.EMAIL || "";
  const companyName = user?.companyName || user?.COMPANY_NAME || "";
  const countryId = user?.countryId || user?.LT_COUNTRY_ID || "";
  const countryName = user?.countryName || user?.COUNTRY_NAME || "";
  const companyAbbreviation =
    user?.companyAbbreviation || user?.COMPANY_ABBRIVIATION || "";

  const isSetupCompleted =
    user?.SETUP_COMPLETED === "true" || 
    user?.SETUP_COMPLETED === true || 
    user?.setupCompleted === true;

  const serviceUrl = user?.URL || user?.url || "";

  // ۱. بررسی وضعیت لایسنس خریداری‌شده / واقعی
  const hasActivePaidLicense = useMemo(() => {
    const isActive = user?.licenseIsActive || user?.LICENSE_IS_ACTIVE === "true" || user?.LICENSE_IS_ACTIVE === true;
    const isExpired = user?.hasExpired || user?.HAS_EXPIRED === "true" || user?.HAS_EXPIRED === true;
    
    // اگر صریحا لایسنس فعال باشد و منقضی نشده باشد
    if (isActive && !isExpired) {
      return true;
    }
    
    // بررسی کمکی از طریق روزهای باقی‌مانده لایسنس واقعی
    if (user?.REMAINING_DAYS !== undefined && user?.REMAINING_DAYS !== null) {
      const days = Number(user.REMAINING_DAYS);
      if (!Number.isNaN(days) && days > 0 && !isExpired) {
        return true;
      }
    }
    
    return false;
  }, [user]);

  // ۲. تاریخ‌های شروع و پایان تریال و لایسنس واقعی
  const trialStartsAt = user?.TRIAL_START_DATE || user?.trialStartDate || null;
  const trialEndsAt = user?.TRIAL_END_DATE || user?.trialEndDate || null;

  const licenseStartsAt = user?.ACTIVATION_DATE || user?.activationDate || null;
  const licenseEndsAt = user?.EXPIRATION_DATE || user?.expirationDate || null;

  // انتخاب تاریخ‌های مناسب برای نمایش در کارت‌ها بر اساس نوع لایسنس فعلی
  const activeStartDate = hasActivePaidLicense ? licenseStartsAt : trialStartsAt;
  const activeEndDate = hasActivePaidLicense ? licenseEndsAt : trialEndsAt;

  const isProfileIncomplete =
    isEmptyString(companyAbbreviation) || isEmptyString(countryId);

  // بررسی وجود حداقل یکی از داده‌های لایسنس یا تریال برای هدایت به آنبردینگ
  const hasServiceData = useMemo(() => {
    return (
      !isEmptyString(serviceUrl) ||
      !isEmptyString(trialStartsAt) ||
      !isEmptyString(licenseStartsAt) ||
      hasActivePaidLicense
    );
  }, [serviceUrl, trialStartsAt, licenseStartsAt, hasActivePaidLicense]);

  // ۳. بررسی انقضا (اگر لایسنس پولی فعال دارد منقضی نیست، در غیر این صورت تریال را بررسی کن)
  const isExpired = useMemo(() => {
    if (hasActivePaidLicense) {
      return false;
    }

    // بررسی انقضای تریال از روی فیلدهای روز باقی‌مانده یا تاریخ پایان تریال
    if (user?.REMAINING_DAYS !== undefined && user?.REMAINING_DAYS !== null) {
      const days = Number(user.REMAINING_DAYS);
      if (!Number.isNaN(days) && days <= 0) {
        return true;
      }
    }

    if (user?.HAS_EXPIRED === "true" || user?.HAS_EXPIRED === true) {
      return true;
    }

    if (!trialEndsAt) return false;

    // تبدیل فرمت "YYYY/MM/DD" به ساختار استاندارد برای مقایسه تاریخی درست
    const normalizedTrialDate = String(trialEndsAt).replace(/\//g, "-");
    const parsed = new Date(normalizedTrialDate).getTime();
    if (Number.isNaN(parsed)) return false;

    return parsed < Date.now();
  }, [user, hasActivePaidLicense, trialEndsAt]);

  const credentials = useMemo(() => {
    let finalUrl = user?.SYSTEM_URL || user?.URL || user?.url || "";
    
    if (isEmptyString(finalUrl) && companyAbbreviation) {
      finalUrl = `https://app.dynaship.io/${String(companyAbbreviation).toLowerCase()}`;
    } else if (!isEmptyString(finalUrl) && !finalUrl.startsWith("http")) {
      finalUrl = `https://${finalUrl}`;
    }

    return {
      url: finalUrl,
      username:
        user?.SYSTEM_USERNAME ||
        user?.USER_NAME ||
        user?.USERNAME ||
        email ||
        "",
      password:
        user?.SYSTEM_PASSWORD ||
        user?.PASSWORD ||
        "",
    };
  }, [user, companyAbbreviation, email]);

  const summary = (
    <SubscriptionSummaryCard
      companyName={companyName}
      status={isExpired ? "expired" : hasActivePaidLicense ? "active_paid" : "active_trial"}
      endsAt={isExpired && licenseEndsAt ? licenseEndsAt : activeEndDate}
      isPaidLicense={hasActivePaidLicense || Boolean(isExpired && licenseEndsAt)}
    />
  );

  const InfoItem = ({ label, value }) => (
    <Box
      sx={{
        p: 2,
        borderRadius: "var(--admin-radius)",
        bgcolor: "var(--surface-muted)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography variant="body1" fontWeight={500}>
        {value || "-"}
      </Typography>
    </Box>
  );

  if (!user) {
    return null;
  }

  // اگر کاربر هنوز هیچ دیتایی (نه تریال و نه لایسنس) نداشته باشد، به onboarding هدایت می‌شود
  if (!hasServiceData) {
    return <Navigate to="/" replace />;
  }

  if (isProfileIncomplete) {
    return (
      <Box className="license-requests-page" sx={sx.page}>
        <AdminPageHeader icon={ClipboardCheck} title={t("sidebar.licenseRequests", "License Requests")} description={t("licenseRequests.headerDescription")} />
        <Box sx={{ width: "100%" }}>
          <Stack spacing={3}>
            {summary}
            <Alert severity="warning">
              {t("licenses.license-requests.profile_incomplete_alert")}
            </Alert>

            <Card variant="outlined" sx={{ textAlign: "center" }}>
              <CardContent>
              <Stack spacing={2} alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  {t("licenses.license-requests.profile_incomplete_title")}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  {t("licenses.license-requests.profile_incomplete_message")}
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  href="/dashboard/profile"
                >
                  {t("licenses.license-requests.profile_incomplete_button")}
                </Button>
              </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </Box>
    );
  }

  /**
   * حالت فعال (چه Trial فعال باشد و چه لایسنس واقعی خریداری شده باشد)
   */
  if (!isExpired) {
    return (
      <Box className="license-requests-page" sx={sx.page}>
        <AdminPageHeader icon={ClipboardCheck} title={t("sidebar.licenseRequests", "License Requests")} description={t("licenseRequests.headerDescription")} />
        <Box sx={{ width: "100%" }}>
          <Stack spacing={3}>
            {summary}

            <Card variant="outlined">
              <CardContent>
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight={700}>
                  {hasActivePaidLicense 
                    ? t("licenses.license-requests.active_paid_info", "License Information") 
                    : t("licenses.license-requests.active_info")}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                    },
                    gap: 2,
                  }}
                >
                  <InfoItem
                    label={t("licenses.license-requests.company_name")}
                    value={companyName}
                  />

                  <InfoItem
                    label={t("licenses.license-requests.full_name")}
                    value={fullName}
                  />

                  <InfoItem
                    label={t("licenses.license-requests.email")}
                    value={email}
                  />

                  <InfoItem
                    label={t("licenses.license-requests.partition")}
                    value={user?.PARTITION_NAME}
                  />

                  <InfoItem
                    label={t("licenses.license-requests.country")}
                    value={countryName}
                  />

                  <InfoItem
                    label={
                      hasActivePaidLicense
                        ? t("licenses.license-requests.license_start_date", "License Start Date")
                        : t("licenses.license-requests.trial_start_date")
                    }
                    value={activeStartDate}
                  />

                  <InfoItem
                    label={
                      hasActivePaidLicense
                        ? t("licenses.license-requests.license_end_date", "License End Date")
                        : t("licenses.license-requests.trial_end_date")
                    }
                    value={activeEndDate}
                  />

                  <InfoItem
                    label={t("licenses.license-requests.setup_completed")}
                    value={
                      isSetupCompleted
                        ? t("common.yes")
                        : t("common.no")
                    }
                  />

                  <InfoItem
                    label={t("licenses.license-requests.service_url")}
                    value={
                      serviceUrl ? (
                        <a
                          href={
                            String(serviceUrl).startsWith("http")
                              ? serviceUrl
                              : `https://${serviceUrl}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "var(--primary)",
                            textDecoration: "none",
                            wordBreak: "break-all",
                          }}
                        >
                          {serviceUrl}
                        </a>
                      ) : (
                        "-"
                      )
                    }
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "stretch", sm: "flex-end" },
                    pt: 1,
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    href="/dashboard/licenses"
                    sx={{ minWidth: 220 }}
                  >
                    {t("licenses.license-requests.view_licenses_btn")}
                  </Button>
                </Box>
              </Stack>
              </CardContent>
            </Card>

            {credentials?.password && (
              <SystemCredentialsCard credentials={credentials} />
            )}

          </Stack>
        </Box>
      </Box>
    );
  }

  /**
   * حالت Trial منقضی شده و هنوز لایسنس جدید نخریده است
   */
  return (
    <Box className="license-requests-page" sx={sx.page}>
      <AdminPageHeader icon={ClipboardCheck} title={t("sidebar.licenseRequests", "License Requests")} description={t("licenseRequests.headerDescription")} />
      <Box sx={{ width: "100%" }}>
        <Stack spacing={3}>
          {summary}
          <TrialExpiredCard
            companyName={companyName}
            trialEndsAt={trialEndsAt}
          />

          <LicensePurchaseForm
            defaultValues={{
              companyName,
              companyAbbreviation,
              email,
              fullName,
              coreUsers: user?.DYNASHIP_CORE || user?.dynashipCore || 1,
              enterpriseUsers: user?.DYNASHIP_ENTERPRISE || user?.dynashipEnterprise || 0,
              agentPortalUsers: user?.DYNASHIP_AGENT_PORTAL_USER || user?.dynashipAgentPortalUser || 0,
              customerPortalUsers: user?.DYNASHIP_CUSTOMER_PORTAL_USER || user?.dynashipCustomerPortalUser || 0,
              functionalUsers: user?.DYNASHIP_FUNCTIONAL_USER || user?.dynashipFunctionalUser || 0,
              additionalCompany: user?.DYNASHIP_ADDITIONAL_COMPANY || user?.dynashipAdditionalCompany || 0,
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default LicenseRequestsPage;

export const sx = {
  page: {
    width: "100%",
    px: 0,
    py: 0,
  },
};
